import styles from "./ConditionRow.module.scss";
import { Dropdown } from "@/frontend/shared/ui/Dropdown";
import { TrashIcon } from "@/frontend/shared/assets/icons";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Tag } from "@/frontend/shared/ui/TagV2";
import { filterLocations, nonUItypes } from "@/frontend/components/FilterV2/const/const";
import { Input } from "@/frontend/shared/ui/Input";
import { CurrencyInput } from "@/frontend/shared/ui/CurrencyInput";
import {
  convertToMoneyAmount,
  parseMoneyAmount,
  validateMoneyAmount
} from "@/frontend/shared/lib/validations/validateMoneyAmount";
import clsx from "clsx";
import { Typography } from "@/frontend/shared/ui/Typography";
import {
  typesMap,
  type ConditionRowFilter,
  type ConditionRowOptions,
  ConditionType,
  OperandType
} from "../../model/types";
import { companyStatus } from "@/frontend/shared/const/const";

const mapOperands: Record<ConditionType, { label: string; value: OperandType }[]> = {
  tag: [
    { label: "includes", value: "includes" },
    { label: "doesn't include", value: "do_not_includes" }
  ],
  location: [
    { label: "is", value: "is" },
    { label: "is not", value: "is_not" }
  ],
  financialStage: [
    { label: "is", value: "is" },
    { label: "is not", value: "is_not" }
  ],
  funding: [
    { label: "greater than", value: "greater_than" },
    { label: "less than", value: "less_than" }
  ],
  foundingYear: [
    { label: "greater than", value: "greater_than" },
    { label: "less than", value: "less_than" }
  ],
  lastFundingDate: [
    { label: "greater than", value: "greater_than" },
    { label: "less than", value: "less_than" }
  ],
  lastFundingYear: [
    { label: "greater than", value: "greater_than" },
    { label: "less than", value: "less_than" }
  ],
  description: [],
  group: []
};

interface ConditionRowProps extends ConditionRowFilter {
  label: string;
  onConditionChange?: (condition: ConditionRowFilter) => void;
  onDelete: () => void;
  options?: ConditionRowOptions;
  linked?: boolean;
  prefixId?: string;
  wrap?: boolean;
}

const { PrivateEquity, ...realCompanyStatus } = companyStatus;

export function ConditionRow({
  options: optionsProp,
  id,
  label,
  value,
  onDelete,
  onConditionChange,
  type,
  operand,
  linked,
  error,
  isOr,
  prefixId = "",
  wrap
}: ConditionRowProps) {
  let options: { label: string; value: string }[] = [];
  switch (type) {
    case "tag":
      options = optionsProp?.["tag"] || [];
      break;
    case "location":
      options = filterLocations.map((l) => ({
        value: l,
        label: l
      }));
      break;
    case "financialStage":
      options = Object.keys(realCompanyStatus).map((l) => ({
        value: l.replace(/\s/g, ""),
        label: realCompanyStatus[l]
      }));
      break;
    case "lastFundingDate":
      options = [
        { label: "3 months ago", value: "3" },
        { label: "6 months ago", value: "6" },
        { label: "1 year ago", value: "12" },
        { label: "3 years ago", value: "36" }
      ];
      break;
  }
  const isHandledOption =
    type === "tag" || type === "location" || type === "financialStage" || type === "lastFundingDate";

  const fieldIdStart = prefixId + "filtersV2-" + (isOr ? "matchAny-" : "matchAll-");

  return (
    <div className={styles.rowContainer}>
      <div className={clsx(styles.row)}>
        <Tag className={clsx(fieldIdStart + "label", styles.tag, linked && styles.linked, wrap && styles.noAfter)}>
          {label}
        </Tag>

        <div className={clsx(styles.rowControls, wrap && styles.wrap)}>
          <Dropdown
            className={clsx(styles.dropdown, fieldIdStart + "type", wrap && styles.grow)}
            containerClassName={clsx(styles.dropdown, wrap && styles.growContainer)}
            inputSize="xs"
            options={Object.keys(typesMap)
              .filter((k) => !nonUItypes.includes(k))
              .map((k) => ({
                value: k,
                label: typesMap[k as ConditionType]
              }))}
            value={type}
            onChange={(v) => {
              const newValue = v === "type" ? value : undefined;
              const newOperand = v === "type" ? operand : mapOperands[v as ConditionType]?.[0].value;
              onConditionChange?.({
                value: newValue,
                type: v as ConditionType,
                operand: newOperand as OperandType,
                isOr,
                id
              });
            }}
            getValueLabel={(v) => (v ? v.label : "")}
          />
          <Dropdown
            inputSize="xs"
            className={clsx(styles.dropdown, fieldIdStart + "operand", wrap && styles.grow)}
            containerClassName={clsx(styles.dropdown, wrap && styles.growContainer)}
            options={mapOperands[type]}
            value={operand}
            onChange={(v) => {
              onConditionChange?.({ value, type, operand: v as OperandType, isOr, id });
            }}
            getValueLabel={(v) => (v ? v.label : "")}
          />
          {isHandledOption && (
            <Dropdown
              inputSize="xs"
              className={clsx(styles.bigDropdown, fieldIdStart + "value", wrap && styles.grow)}
              containerClassName={clsx(styles.bigDropdown, wrap && styles.growContainer)}
              options={options}
              value={value}
              onChange={(v) => {
                onConditionChange?.({ value: v, type, operand, isOr, id });
              }}
              getValueLabel={(v) => (v ? v.label : "")}
              search
            />
          )}

          {(type === "foundingYear" || type === "lastFundingYear") && (
            <Input
              placeholder=""
              value={value || ""}
              onChange={(e) => {
                onConditionChange?.({ value: e.target.value, type, operand, isOr, id });
              }}
              inputSize="xs"
              className={clsx(styles.bigDropdown, fieldIdStart + "value")}
            />
          )}

          {type === "funding" && (
            <CurrencyInput
              value={convertToMoneyAmount(value || "")}
              onChange={(e) => {
                onConditionChange?.({ value: parseMoneyAmount(e as string).toString(), type, operand, isOr, id });
              }}
              inputSize="xs"
              validator={(s) => validateMoneyAmount(s, true)}
              className={clsx(styles.bigDropdown, fieldIdStart + "value")}
            />
          )}

          <NormalButton
            onClick={onDelete}
            variant="tertiary"
            size="small"
            color="greyscale700"
            className={clsx(styles.deleteBtn, fieldIdStart + "deleteButton")}
          >
            <TrashIcon />
          </NormalButton>
        </div>
      </div>
      {error && (
        <Typography className={styles.errorMessage} variant="label-5" color="error-red">
          {error}
        </Typography>
      )}
    </div>
  );
}
